import { FileText, Presentation, BookOpen, MessageSquare, Wand2, Download, Sparkles } from "lucide-react";

const features = [
  {
    icon: FileText,
    title: "Text Extraction",
    description: "Converts your handwritten notes into clean, editable digital text using advanced OCR.",
    color: "from-blue-500/15 to-blue-400/5 text-blue-600",
    border: "group-hover:border-blue-500/30",
  },
  {
    icon: Wand2,
    title: "Assignment Generator",
    description: "AI generates complete assignment answers based on your notes and topic.",
    color: "from-purple-500/15 to-purple-400/5 text-purple-600",
    border: "group-hover:border-purple-500/30",
  },
  {
    icon: Presentation,
    title: "PPT Creator",
    description: "Automatically creates presentation slides from your notes with proper formatting.",
    color: "from-orange-500/15 to-orange-400/5 text-orange-600",
    border: "group-hover:border-orange-500/30",
  },
  {
    icon: BookOpen,
    title: "Long & Short Answers",
    description: "Get detailed explanations or concise notes — perfect for exams.",
    color: "from-green-500/15 to-green-400/5 text-green-600",
    border: "group-hover:border-green-500/30",
  },
  {
    icon: MessageSquare,
    title: "Topic Explainer",
    description: "Don't understand something? AI explains any concept in simple terms.",
    color: "from-pink-500/15 to-pink-400/5 text-pink-600",
    border: "group-hover:border-pink-500/30",
  },
  {
    icon: Download,
    title: "Export Anywhere",
    description: "Download as PDF, Word, or PPT. Share directly to Google Drive.",
    color: "from-teal-500/15 to-teal-400/5 text-teal-600",
    border: "group-hover:border-teal-500/30",
  },
];

const Features = () => {
  return (
    <section id="features" className="py-24 bg-muted/30 relative overflow-hidden">
      {/* Background decorations */}
      <div className="absolute top-20 right-0 w-[400px] h-[400px] bg-primary/[0.03] rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-20 left-0 w-[300px] h-[300px] bg-accent/[0.03] rounded-full blur-3xl pointer-events-none" />
      
      <div className="container mx-auto px-4">
        <div className="text-center mb-16 relative z-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-semibold mb-4">
            <Sparkles className="w-4 h-4" />
            Powerful Features
          </div>
          <h2 className="text-3xl md:text-5xl font-extrabold text-foreground mt-2 mb-4">
            Everything You Need to Ace Your Studies
          </h2>
          <p className="text-muted-foreground max-w-xl mx-auto text-lg">
            From messy notes to polished assignments in minutes. 
            Our AI does the heavy lifting so you can focus on learning.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto relative z-10">
          {features.map((feature, index) => (
            <div
              key={feature.title}
              className={`group bg-card rounded-3xl p-7 shadow-card hover:shadow-glow transition-all duration-500 hover:scale-[1.02] border border-border/40 ${feature.border} relative overflow-hidden`}
              style={{ animationDelay: `${index * 100}ms` }}
            >
              {/* Gradient overlay on hover */}
              <div className={`absolute inset-0 bg-gradient-to-br ${feature.color} opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-3xl`} />
              
              <div className="relative z-10">
                <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${feature.color} flex items-center justify-center mb-5 group-hover:scale-110 group-hover:shadow-soft transition-all duration-500`}>
                  <feature.icon className="w-7 h-7" />
                </div>
                <h3 className="text-lg font-bold text-foreground mb-2">
                  {feature.title}
                </h3>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  {feature.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Features;
